/**
 * InviteNameInput Component
 *
 * Input field for naming an invite. When a name is entered, all selections
 * in the modal are automatically saved and can be restored later.
 *
 * Features:
 * - Optional field (blank = no auto-save)
 * - Load existing invite from dropdown
 * - Shows saved indicator when auto-saving
 * - Delete saved invites
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Bookmark,
  ChevronDown,
  Trash2,
  Check,
  Loader2,
  Clock,
  CheckCircle2,
  X,
} from 'lucide-react';
import type { SavedInvite } from '../types/emailTemplates';

interface InviteNameInputProps {
  /** Current invite name */
  value: string;
  /** Called when name changes */
  onChange: (name: string) => void;
  /** List of saved invites to show in dropdown */
  savedInvites: SavedInvite[];
  /** Currently active invite ID */
  activeInviteId: string | null;
  /** Called when user selects a saved invite */
  onLoadInvite: (invite: SavedInvite) => void;
  /** Called when user wants to delete a saved invite */
  onDeleteInvite: (id: string) => void;
  /** Called when user wants to start fresh (clear active invite) */
  onClearInvite: () => void;
  /** Whether an invite is currently being saved */
  isSaving?: boolean;
}

/**
 * Format date for display (mm.dd.yyyy hh:mm am/pm)
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;

  return `${month}.${day}.${year} ${displayHours}:${minutes} ${ampm}`;
}

export const InviteNameInput: React.FC<InviteNameInputProps> = ({
  value,
  onChange,
  savedInvites,
  activeInviteId,
  onLoadInvite,
  onDeleteInvite,
  onClearInvite,
  isSaving = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setConfirmDeleteId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleSelectInvite = useCallback((invite: SavedInvite) => {
    onLoadInvite(invite);
    setShowDropdown(false);
  }, [onLoadInvite]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirmDeleteId === id) {
      onDeleteInvite(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
    }
  }, [confirmDeleteId, onDeleteInvite]);

  const handleClearClick = useCallback(() => {
    onChange('');
    onClearInvite();
  }, [onChange, onClearInvite]);

  // Get active invite if any
  const activeInvite = savedInvites.find((inv) => inv.id === activeInviteId);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
        <Bookmark className="w-4 h-4 text-cyan-400" />
        Invite Name
        <span className="text-xs text-dark-muted font-normal">(optional)</span>
      </label>

      <div className="flex items-center gap-2">
        {/* Input field */}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter name to save selections..."
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border/30 rounded-xl text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-cyan-500 pr-24"
          />

          {/* Status indicator inside input */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {isSaving && (
              <span className="flex items-center gap-1 text-xs text-cyan-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving
              </span>
            )}
            {!isSaving && value && activeInviteId && (
              <span className="flex items-center gap-1 text-xs text-green-400">
                <Check className="w-3 h-3" />
                Saved
              </span>
            )}
            {value && (
              <button
                onClick={handleClearClick}
                className="p-1 text-dark-muted hover:text-red-400 transition-all duration-150"
                title="Clear and start fresh"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Dropdown button - only show if there are saved invites */}
        {savedInvites.length > 0 && (
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className={`flex items-center gap-1 px-3 py-2 border rounded-lg text-sm transition-all duration-150 ${
              showDropdown
                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                : 'bg-dark-bg border-dark-border/30 text-dark-muted hover:text-white hover:border-dark-border/30'
            }`}
          >
            <span className="hidden sm:inline">Load</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Active invite indicator */}
      {activeInvite && (
        <div className="mt-2 flex items-center gap-2 text-xs text-dark-muted">
          <Clock className="w-3 h-3" />
          <span>Last updated: {formatDate(activeInvite.updatedAt)}</span>
          {activeInvite.isCompleted && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
              <CheckCircle2 className="w-3 h-3" />
              Completed
            </span>
          )}
        </div>
      )}

      {/* Dropdown list */}
      {showDropdown && savedInvites.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-30 max-h-64 overflow-y-auto">
          <div className="p-2 border-b border-dark-border/30">
            <span className="text-xs text-dark-muted">Recent Invites ({savedInvites.length})</span>
          </div>

          {savedInvites.map((invite) => (
            <div
              key={invite.id}
              onClick={() => handleSelectInvite(invite)}
              className={`flex items-center justify-between px-3 py-2 hover:bg-dark-border/50 transition-all duration-150 cursor-pointer ${
                invite.id === activeInviteId ? 'bg-cyan-500/10' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">{invite.name}</span>
                  {invite.isCompleted && (
                    <CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" />
                  )}
                  {invite.id === activeInviteId && (
                    <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-[10px]">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-dark-muted">
                  <span>{formatDate(invite.updatedAt)}</span>
                  {invite.recipients.length > 0 && (
                    <span>• {invite.recipients.length} recipients</span>
                  )}
                  {invite.exportHistory.length > 0 && (
                    <span>• {invite.exportHistory.length} exports</span>
                  )}
                </div>
              </div>

              {/* Delete button */}
              <button
                onClick={(e) => handleDeleteClick(e, invite.id)}
                className={`p-1.5 rounded transition-all duration-150 shrink-0 ${
                  confirmDeleteId === invite.id
                    ? 'bg-red-500 text-white'
                    : 'text-dark-muted hover:text-red-400 hover:bg-red-500/10'
                }`}
                title={confirmDeleteId === invite.id ? 'Click again to confirm' : 'Delete invite'}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Help text */}
      {!value && !activeInviteId && (
        <p className="mt-2 text-xs text-dark-muted">
          Name your invite to auto-save all selections, recipients, and export history
        </p>
      )}
    </div>
  );
};

export default InviteNameInput;
